import { SocialProfileFieldsFragment, TimezoneSetting, UserOrganizationRole } from '../models/graphql';
import { GetOrganization_organization, GetOrganization_organization_workspaces_users } from '../models/legacy-graphql';
import { SettingsTeamMemberStatusEnum } from '../constants/team-members';
import { SettingsTeamMember, SettingsWorkspace } from '../models/settings-pages';

export const mapOrganizationInvitationsToTeamMembersType = (
  organization: GetOrganization_organization
): SettingsTeamMember[] => {
  const teamMembers: SettingsTeamMember[] = [];
  organization?.userInvitations.forEach(invite => {
    const tm: SettingsTeamMember = {
      id: invite.id,
      name: 'Team Member',
      email: invite.email,
      status: SettingsTeamMemberStatusEnum.INVITED,
      role: invite.role.toUpperCase() as UserOrganizationRole,
      workspaces: invite.workspaceIds,
      invitationToken: invite.token
    };

    teamMembers.push(tm);
  });

  return teamMembers;
};

export const mapOrganizationUsersToTeamMembersType = (
  organization: GetOrganization_organization
): SettingsTeamMember[] => {
  const teamMembers: SettingsTeamMember[] = [];

  organization?.userAssignments.forEach(assignment => {
    const workspaceIds: string[] = [];

    organization?.workspaces.forEach(workspace => {
      const index = workspace.users.findIndex(user => user.id === assignment.user.id);

      if (index !== -1) {
        workspaceIds.push(workspace.id);
      }
    });

    const teamMember: SettingsTeamMember = {
      id: assignment.user.id,
      name: assignment.user.firstName,
      email: assignment.user.email,
      status: SettingsTeamMemberStatusEnum.MEMBER,
      role: assignment.role,
      workspaces: workspaceIds
    };

    teamMembers.push(teamMember);
  });

  return teamMembers;
};

// Gets a list of Users of a workspace, and retrieves their
// SettingsTeamMember type from a list of all the users from that Org
export const addWorkspaceUsersAsTeamMembers = (
  workspaceUsers: GetOrganization_organization_workspaces_users[],
  teamMembers: SettingsTeamMember[]
): SettingsTeamMember[] => {
  const members: SettingsTeamMember[] = [];

  workspaceUsers.forEach(user => {
    const member = teamMembers.find(member => member.id === user.id);

    if (member) {
      members.push(member);
    }
  });

  return members;
};

export const addWorkspacesToWorkspaceType = (organization: GetOrganization_organization): SettingsWorkspace[] => {
  let allOrgMembers: SettingsTeamMember[] = [];
  const workspaces: SettingsWorkspace[] = [];

  allOrgMembers = [
    ...mapOrganizationUsersToTeamMembersType(organization),
    ...mapOrganizationInvitationsToTeamMembersType(organization)
  ];

  organization?.workspaces.forEach(workspace => {
    const wsMembers: SettingsTeamMember[] = addWorkspaceUsersAsTeamMembers(workspace.users, allOrgMembers);

    const ws: SettingsWorkspace = {
      id: workspace.id,
      name: workspace.name,
      timezone: workspace.timezone as TimezoneSetting,
      members: sortArrayOfTeamMembers(wsMembers),
      isDeletable: isLastWorkspaceForATeamMember(workspace.id, allOrgMembers),
      profiles: workspace.socialProfiles as SocialProfileFieldsFragment[]
    };

    workspaces.push(ws);
  });

  return workspaces;
};

export const sortArrayOfTeamMembers = (teamMembers: SettingsTeamMember[]): SettingsTeamMember[] => {
  return teamMembers?.sort((a, b) => {
    if (a.role === b.role) {
      return 0;
    }
    if (
      a.role === UserOrganizationRole.OWNER ||
      (a.role === UserOrganizationRole.ADMIN && b.role === UserOrganizationRole.EDITOR)
    ) {
      return -1;
    }
    return 1;
  });
};

export const isLastWorkspaceForATeamMember = (workspaceId: string, teamMembers: SettingsTeamMember[]): boolean => {
  let isLastWorkspace = false;

  teamMembers.forEach(user => {
    const numberOfAssignedWorkspaces = user.workspaces?.length || 0;
    if (user.workspaces?.includes(workspaceId) && numberOfAssignedWorkspaces < 2) {
      isLastWorkspace = true;
    }
  });

  return !isLastWorkspace;
};
